import React, { useState } from 'react';

interface DeploymentStep {
  id: number;
  phase: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  details?: any;
  timestamp?: string;
  duration?: number;
  command?: string;
  output?: string;
  url?: string;
}

export const DeploymentSteps: React.FC = () => {
  const [deploymentType, setDeploymentType] = useState<'api' | 'web' | null>(null);
  const [steps, setSteps] = useState<DeploymentStep[]>([]);
  const [isDeploying, setIsDeploying] = useState(false);

  const simulateAPIDeployment = async () => {
    setIsDeploying(true);
    setSteps([]);
    setDeploymentType('api');

    const deploySteps: DeploymentStep[] = [
      // PHASE 1: LOCAL BUILD
      {
        id: 1,
        phase: '1️⃣ LOCAL BUILD',
        title: 'TypeScript Compilation',
        description: 'Compiling TypeScript to JavaScript',
        status: 'pending',
        command: 'tsc',
        details: {
          files: ['src/index.ts', 'src/routes/*.ts', 'src/types.ts'],
          outputDir: 'dist/',
          tsconfig: 'tsconfig.json'
        }
      },
      {
        id: 2,
        phase: '1️⃣ LOCAL BUILD',
        title: 'Bundle Dependencies',
        description: 'Bundling with esbuild for Workers',
        status: 'pending',
        command: 'wrangler build',
        details: {
          entryPoint: 'src/index.ts',
          format: 'esm',
          target: 'es2022',
          bundle: true,
          minify: true
        }
      },

      // PHASE 2: AUTHENTICATION
      {
        id: 3,
        phase: '2️⃣ AUTHENTICATION',
        title: 'CloudFlare API Auth',
        description: 'Authenticating with CloudFlare API',
        status: 'pending',
        details: {
          accountId: '9f2fcf619e8e9758a4b7e95c878dd49c',
          email: 'b@oh.mom',
          permissions: ['workers:write', 'd1:write', 'r2:write']
        }
      },

      // PHASE 3: RESOURCE VALIDATION
      {
        id: 4,
        phase: '3️⃣ RESOURCE CHECK',
        title: 'Validate D1 Database',
        description: 'Checking D1 database exists',
        status: 'pending',
        details: {
          database: 'cf-database',
          databaseId: 'f7994da0-0049-4965-8508-2633ebafc511',
          binding: 'DB'
        }
      },
      {
        id: 5,
        phase: '3️⃣ RESOURCE CHECK',
        title: 'Validate R2 Bucket',
        description: 'Checking R2 storage bucket',
        status: 'pending',
        details: {
          bucket: 'cf-storage',
          binding: 'STORAGE',
          region: 'auto'
        }
      },
      {
        id: 6,
        phase: '3️⃣ RESOURCE CHECK',
        title: 'Validate KV Namespace',
        description: 'Checking KV namespace exists',
        status: 'pending',
        details: {
          namespace: 'CACHE',
          namespaceId: 'e4ed006f200f4298b2a42c6b35e8a081'
        }
      },

      // PHASE 4: UPLOAD
      {
        id: 7,
        phase: '4️⃣ UPLOAD',
        title: 'Upload Worker Bundle',
        description: 'Uploading compiled code to CloudFlare',
        status: 'pending',
        details: {
          size: '519.66 KiB',
          gzipSize: '100.52 KiB',
          files: 42
        }
      },
      {
        id: 8,
        phase: '4️⃣ UPLOAD',
        title: 'Configure Environment',
        description: 'Setting environment variables and secrets',
        status: 'pending',
        details: {
          bindings: ['DB', 'STORAGE', 'CACHE'],
          compatibilityDate: '2024-01-01',
          environment: 'production'
        }
      },

      // PHASE 5: DEPLOYMENT
      {
        id: 9,
        phase: '5️⃣ DEPLOYMENT',
        title: 'Deploy to Edge Network',
        description: 'Deploying to CloudFlare global network',
        status: 'pending',
        details: {
          regions: ['North America', 'Europe', 'Asia-Pacific'],
          datacenters: 285,
          deploymentId: 'aa191038-dd11-47db-94e9-651587c91e46'
        }
      },
      {
        id: 10,
        phase: '5️⃣ DEPLOYMENT',
        title: 'Configure Routes',
        description: 'Setting up worker routes and triggers',
        status: 'pending',
        url: 'https://cf-api.b-9f2.workers.dev',
        details: {
          pattern: '*.b-9f2.workers.dev/*',
          zone: 'workers.dev'
        }
      },

      // PHASE 6: VERIFICATION
      {
        id: 11,
        phase: '6️⃣ VERIFICATION',
        title: 'Health Check',
        description: 'Verifying worker is responding',
        status: 'pending',
        command: 'curl https://cf-api.b-9f2.workers.dev/health',
        details: {
          endpoint: '/health',
          expectedStatus: 200,
          timeout: 5000
        }
      },
      {
        id: 12,
        phase: '✅ COMPLETE',
        title: 'Deployment Successful',
        description: 'Worker is live on CloudFlare Edge',
        status: 'pending',
        url: 'https://cf-api.b-9f2.workers.dev',
        details: {
          startupTime: '5ms',
          version: 'aa191038-dd11-47db-94e9-651587c91e46',
          url: 'https://cf-api.b-9f2.workers.dev'
        }
      }
    ];

    // Simulate deployment progression
    for (let i = 0; i < deploySteps.length; i++) {
      const step = deploySteps[i];

      // Add step
      setSteps(prev => [...prev, { ...step, status: 'in-progress', timestamp: new Date().toISOString() }]);

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 700));

      // Complete step
      setSteps(prev => prev.map((s, idx) =>
        idx === i
          ? {
              ...s,
              status: 'completed',
              duration: 800 + Math.random() * 700,
              output: generateOutput(s)
            }
          : s
      ));
    }

    setIsDeploying(false);
  };

  const simulateWebDeployment = async () => {
    setIsDeploying(true);
    setSteps([]);
    setDeploymentType('web');

    const deploySteps: DeploymentStep[] = [
      // PHASE 1: BUILD
      {
        id: 1,
        phase: '1️⃣ BUILD',
        title: 'Install Dependencies',
        description: 'Installing node_modules',
        status: 'pending',
        command: 'npm install',
        details: {
          packages: 909,
          added: 43,
          audited: true
        }
      },
      {
        id: 2,
        phase: '1️⃣ BUILD',
        title: 'TypeScript Check',
        description: 'Type checking React components',
        status: 'pending',
        command: 'tsc --noEmit',
        details: {
          components: 32,
          files: ['*.tsx', '*.ts'],
          strict: true
        }
      },
      {
        id: 3,
        phase: '1️⃣ BUILD',
        title: 'Vite Build',
        description: 'Building production bundle',
        status: 'pending',
        command: 'vite build',
        details: {
          modules: 502,
          chunks: ['index.js', 'vendor.js'],
          optimization: 'minified'
        }
      },

      // PHASE 2: OPTIMIZE
      {
        id: 4,
        phase: '2️⃣ OPTIMIZE',
        title: 'Bundle Analysis',
        description: 'Analyzing bundle size',
        status: 'pending',
        details: {
          'index.js': '539.73 kB',
          'index.css': '36.39 kB',
          gzipped: '161.04 kB total'
        }
      },
      {
        id: 5,
        phase: '2️⃣ OPTIMIZE',
        title: 'Asset Processing',
        description: 'Optimizing images and static assets',
        status: 'pending',
        details: {
          images: 'compressed',
          css: 'minified',
          js: 'tree-shaken'
        }
      },

      // PHASE 3: CLOUDFLARE PAGES
      {
        id: 6,
        phase: '3️⃣ PAGES SETUP',
        title: 'Authenticate CloudFlare',
        description: 'Logging into CloudFlare Pages',
        status: 'pending',
        details: {
          account: 'b@oh.mom',
          projectName: 'cf-web'
        }
      },
      {
        id: 7,
        phase: '3️⃣ PAGES SETUP',
        title: 'Create/Update Project',
        description: 'Setting up Pages project',
        status: 'pending',
        command: 'wrangler pages project create cf-web',
        details: {
          branch: 'main',
          buildCommand: 'npm run build',
          outputDir: 'dist'
        }
      },

      // PHASE 4: UPLOAD
      {
        id: 8,
        phase: '4️⃣ UPLOAD',
        title: 'Upload Static Files',
        description: 'Uploading dist/ to CloudFlare',
        status: 'pending',
        details: {
          files: 4,
          'index.html': '0.47 kB',
          'assets/': '576.12 kB',
          uploadTime: '3.00 sec'
        }
      },

      // PHASE 5: CDN DEPLOYMENT
      {
        id: 9,
        phase: '5️⃣ CDN DEPLOY',
        title: 'Deploy to Edge Network',
        description: 'Distributing to global CDN',
        status: 'pending',
        details: {
          cdn: 'CloudFlare Global Network',
          locations: 285,
          caching: 'enabled'
        }
      },
      {
        id: 10,
        phase: '5️⃣ CDN DEPLOY',
        title: 'Configure Custom Domain',
        description: 'Setting up domain routing',
        status: 'pending',
        details: {
          preview: 'ffa7ef13.cf-web-435.pages.dev',
          production: 'cf-web-435.pages.dev',
          custom: 'biig.dev (optional)'
        }
      },

      // PHASE 6: FINALIZE
      {
        id: 11,
        phase: '6️⃣ FINALIZE',
        title: 'SSL Certificate',
        description: 'Provisioning SSL certificate',
        status: 'pending',
        details: {
          type: 'Universal SSL',
          provider: 'CloudFlare',
          autoRenew: true
        }
      },
      {
        id: 12,
        phase: '✅ COMPLETE',
        title: 'Deployment Live',
        description: 'Site is live on CloudFlare Pages',
        status: 'pending',
        url: 'https://cf-web-435.pages.dev',
        details: {
          deploymentId: 'ffa7ef13',
          url: 'https://cf-web-435.pages.dev',
          buildTime: '1.23s'
        }
      }
    ];

    // Simulate deployment
    for (let i = 0; i < deploySteps.length; i++) {
      const step = deploySteps[i];

      setSteps(prev => [...prev, { ...step, status: 'in-progress', timestamp: new Date().toISOString() }]);
      await new Promise(resolve => setTimeout(resolve, 600 + Math.random() * 800));

      setSteps(prev => prev.map((s, idx) =>
        idx === i
          ? { ...s, status: 'completed', duration: 600 + Math.random() * 800 }
          : s
      ));
    }

    setIsDeploying(false);
  };

  const generateOutput = (step: DeploymentStep) => {
    const outputs: { [key: string]: string } = {
      'TypeScript Compilation': '✓ Successfully compiled 42 TypeScript files',
      'Bundle Dependencies': 'Total Upload: 519.66 KiB / gzip: 100.52 KiB',
      'Upload Worker Bundle': 'Uploaded cf-api (3.71 sec)',
      'Deploy to Edge Network': 'Deployed to 285 data centers globally',
      'Health Check': 'HTTP 200 OK - Worker responding',
      'Vite Build': '✓ 502 modules transformed.',
      'Upload Static Files': '✨ Success! Uploaded 4 files (3.00 sec)',
      'SSL Certificate': 'Certificate provisioned successfully'
    };
    return outputs[step.title] || `✓ ${step.title} completed`;
  };

  const resetDeployment = () => {
    setSteps([]);
    setDeploymentType(null);
    setIsDeploying(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">🚀 CloudFlare Deployment Visualizer</h1>
          <p className="text-gray-400">See EVERY SINGLE STEP of the deployment process</p>
        </div>

        {/* Deployment Type Selector */}
        {!deploymentType && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <button
              onClick={simulateAPIDeployment}
              disabled={isDeploying}
              className="p-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all transform hover:scale-105"
            >
              <h2 className="text-2xl font-bold mb-3">⚡ Deploy API Worker</h2>
              <p className="text-blue-200 mb-4">Deploy backend API to CloudFlare Workers</p>
              <div className="text-left space-y-1 text-sm text-blue-100">
                <p>• TypeScript → JavaScript compilation</p>
                <p>• Bundle with dependencies</p>
                <p>• Deploy to global edge network</p>
                <p>• Configure D1, R2, KV bindings</p>
              </div>
            </button>

            <button
              onClick={simulateWebDeployment}
              disabled={isDeploying}
              className="p-8 bg-gradient-to-br from-green-600 to-green-800 rounded-xl hover:from-green-700 hover:to-green-900 transition-all transform hover:scale-105"
            >
              <h2 className="text-2xl font-bold mb-3">🌐 Deploy Web App</h2>
              <p className="text-green-200 mb-4">Deploy React app to CloudFlare Pages</p>
              <div className="text-left space-y-1 text-sm text-green-100">
                <p>• Build React with Vite</p>
                <p>• Optimize bundle size</p>
                <p>• Upload to CloudFlare CDN</p>
                <p>• Configure custom domains</p>
              </div>
            </button>
          </div>
        )}

        {/* Reset Button */}
        {deploymentType && !isDeploying && (
          <button
            onClick={resetDeployment}
            className="mb-6 px-6 py-2 bg-gray-700 rounded hover:bg-gray-600"
          >
            ← Back to Selection
          </button>
        )}

        {/* Deployment Steps */}
        {steps.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">
              {deploymentType === 'api' ? '⚡ API Worker Deployment' : '🌐 Web App Deployment'}
            </h2>

            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`border-2 rounded-lg p-6 transition-all ${
                  step.status === 'completed' ? 'bg-green-900/20 border-green-600' :
                  step.status === 'in-progress' ? 'bg-blue-900/20 border-blue-600 animate-pulse' :
                  step.status === 'error' ? 'bg-red-900/20 border-red-600' :
                  'bg-gray-800 border-gray-700'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-xs font-bold text-gray-400 mb-1">{step.phase}</span>
                    <h3 className="font-bold text-xl">{step.title}</h3>
                    <p className="text-gray-400 mt-1">{step.description}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${
                      step.status === 'completed' ? 'text-green-400' :
                      step.status === 'in-progress' ? 'text-blue-400' :
                      step.status === 'error' ? 'text-red-400' :
                      'text-gray-500'
                    }`}>
                      {step.status === 'completed' ? '✓ COMPLETED' :
                       step.status === 'in-progress' ? '⏳ IN PROGRESS' :
                       step.status === 'error' ? '❌ ERROR' :
                       '⏸ PENDING'}
                    </span>
                    {step.duration && (
                      <p className="text-xs text-gray-500 mt-1">
                        {(step.duration / 1000).toFixed(2)}s
                      </p>
                    )}
                  </div>
                </div>

                {/* Command Display */}
                {step.command && (
                  <div className="mb-3 p-2 bg-black/30 rounded font-mono text-sm text-green-400">
                    $ {step.command}
                  </div>
                )}

                {/* Output Display */}
                {step.output && step.status === 'completed' && (
                  <div className="mb-3 p-2 bg-black/30 rounded font-mono text-xs text-gray-300">
                    {step.output}
                  </div>
                )}

                {/* Details */}
                {step.details && (
                  <div className="mt-3 p-3 bg-black/20 rounded">
                    <pre className="text-xs font-mono text-gray-400 overflow-auto">
                      {JSON.stringify(step.details, null, 2)}
                    </pre>
                  </div>
                )}

                {/* URL */}
                {step.url && step.status === 'completed' && (
                  <div className="mt-3 p-2 bg-blue-900/30 rounded">
                    <p className="text-sm">
                      🔗 URL: <a href={step.url} target="_blank" rel="noopener noreferrer"
                                className="text-blue-400 hover:underline">{step.url}</a>
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Summary */}
            {!isDeploying && steps.every(s => s.status === 'completed') && (
              <div className="mt-8 p-6 bg-gradient-to-br from-green-900/30 to-green-800/20 border-2 border-green-500 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">🎉 Deployment Complete!</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Total Steps</p>
                    <p className="text-xl font-bold">{steps.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Time</p>
                    <p className="text-xl font-bold">
                      {(steps.reduce((acc, s) => acc + (s.duration || 0), 0) / 1000).toFixed(2)}s
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-400">Live URL</p>
                    <p className="text-xl font-bold text-blue-400">
                      {deploymentType === 'api'
                        ? 'https://cf-api.b-9f2.workers.dev'
                        : 'https://cf-web-435.pages.dev'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Architecture Diagram */}
        <div className="mt-12 p-6 bg-gray-800 rounded-lg">
          <h3 className="text-xl font-bold mb-4">📐 Deployment Architecture</h3>
          <pre className="text-xs font-mono text-gray-300">
{`
LOCAL DEVELOPMENT                    CLOUDFLARE INFRASTRUCTURE
┌─────────────┐                     ┌──────────────────────┐
│   VS Code   │                     │   CloudFlare Edge    │
│  Terminal   │                     │   (285 locations)    │
└──────┬──────┘                     └──────────┬───────────┘
       │                                        │
       │ wrangler deploy                        │
       ▼                                        ▼
┌─────────────┐                     ┌──────────────────────┐
│   Build     │                     │   Workers Runtime    │
│  TypeScript │────────UPLOAD──────▶│   - API endpoints    │
│    Vite     │                     │   - Edge computing   │
└─────────────┘                     └──────────────────────┘
       │                                        │
       │                                        │
       ▼                                        ▼
┌─────────────┐                     ┌──────────────────────┐
│   Bundle    │                     │   CloudFlare Pages   │
│  Optimize   │────────CDN─────────▶│   - Static hosting   │
│   Minify    │                     │   - Global CDN       │
└─────────────┘                     └──────────────────────┘
                                               │
                                               ▼
                                    ┌──────────────────────┐
                                    │   Storage & Data     │
                                    │   - D1 Database      │
                                    │   - R2 Storage       │
                                    │   - KV Namespace     │
                                    └──────────────────────┘
`}
          </pre>
        </div>
      </div>
    </div>
  );
};